import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Notify } from 'notiflix';
import { 
  saveValuePackTiers,
  updateValuePackTiers
} from 'store/slices/valuePackSlice';
import { DiscountTypes } from 'config/valuePackFormConfig';

export const useTierManagement = (initialParams = {}) => {
  // CHANGED: Use correct param names
  const { valuePackId: paramValuePackId, mode, durationId: paramDurationId, countryId: paramCountryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use paramValuePackId or fallback to initialParams
  const valuePackId = paramValuePackId || initialParams.valuePackId;
  
  const storedValuePack = useSelector((state) => state.valuePack.currentValuePack);
  const { loading } = useSelector((state) => state.valuePack);
  
  // The pack is loaded on demand (fetchValuePackDetails); the list only has minimal rows.
  const currentValuePack =
    storedValuePack && String(storedValuePack.id) === String(valuePackId) ? storedValuePack : null;
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [tierData, setTierData] = useState({
    Low: createEmptyTier('Low'),
    Mid: createEmptyTier('Mid'),
    High: createEmptyTier('High')
  });

  function createEmptyTier(tierType) {
    return {
      tierType,
      name: '',
      description: '',
      originalPrice: 0,
      currentPrice: 0,
      discountType: DiscountTypes.FLAT,
      flatDiscount: 0,
      percentageDiscount: 0,
      categoryFeatures: [],
      additionalFeatures: [],
      isPopular: false,
      id: null
    };
  }

  // FIXED: Detect mode correctly
  useEffect(() => {
    const isEdit = (paramDurationId && paramCountryId) || mode === 'edit';
    setIsEditMode(isEdit);
    
    if (isEdit) {
      // Set duration and country from params
      if (paramDurationId) {
        setSelectedDuration(paramDurationId);
      }
      
      if (paramCountryId) {
        setSelectedCountry(paramCountryId);
      }
      
      // Load tier data
      if (currentValuePack && currentValuePack.tiers) {
        loadTiersByDurationAndCountry(
          currentValuePack.tiers, 
          paramDurationId, 
          paramCountryId
        );
      }
    } else {
      // Create mode - reset everything
      setIsEditMode(false);
      setSelectedDuration(null);
      setSelectedCountry(null);
      setTierData({
        Low: createEmptyTier('Low'),
        Mid: createEmptyTier('Mid'),
        High: createEmptyTier('High')
      });
    }
  }, [paramDurationId, paramCountryId, mode, currentValuePack]);

  const loadTiersByDurationAndCountry = useCallback((tiers, durationId, countryId) => {
    if (!durationId || !countryId) return;
    
    // Filter tiers by durationId and countryId
    const existingTiers = tiers.filter(tier => 
      tier.durationId === durationId && 
      tier.countryId === countryId
    );

    const updatedTierData = {
      Low: createEmptyTier('Low'),
      Mid: createEmptyTier('Mid'),
      High: createEmptyTier('High')
    };

    existingTiers.forEach(tier => {
      if (updatedTierData[tier.tierType]) {        
        updatedTierData[tier.tierType] = {
          ...updatedTierData[tier.tierType],
          id: tier.id,
          name: tier.name || `${tier.tierType} Tier`,
          description: tier.description || '',
          originalPrice: Number(tier.originalPrice) || 0,
          currentPrice: Number(tier.currentPrice) || 0,
          flatDiscount: tier.discountType === DiscountTypes.FLAT ? Number(tier.discountValue || 0) : 0,
          percentageDiscount: tier.discountType === DiscountTypes.PERCENTAGE ? Number(tier.discountValue || 0) : 0,
          discountType: tier.discountType || DiscountTypes.FLAT,
          categoryFeatures: tier.featuresConfiguration 
            ? tier.featuresConfiguration
                .filter(f => f.featureType === 'global')
                .map(f => ({
                  globalFeatureId: f.featureId,
                  categoryId: f.categoryId
                }))
            : [],
          additionalFeatures: tier.featuresConfiguration 
            ? tier.featuresConfiguration
                .filter(f => f.featureType === 'additional')
                .map(f => ({
                  additionalFeatureId: f.featureId,
                  categoryId: f.categoryId
                }))
            : [],
          isPopular: tier.isPopular || false
        };
      }
    });

    setTierData(updatedTierData);
  }, []);
  
  // Rest of the functions remain the same...
  const handleFeatureToggle = (tierType, categoryId, featureId, isChecked, featureType = 'category') => {    
    if (featureType === 'category') {
      handleCategoryFeatureToggle(tierType, categoryId, featureId, isChecked);
    } else {
      handleAdditionalFeatureToggle(tierType, categoryId, featureId, isChecked);
    }
  };

  const handleCategoryFeatureToggle = (tierType, categoryId, featureId, isChecked) => {    
    setTierData(prev => {
      const currentTier = prev[tierType];
      const newCategoryFeatures = [...(currentTier.categoryFeatures || [])];
      
      if (isChecked) {
        if (!newCategoryFeatures.some(f => f.globalFeatureId === featureId && f.categoryId === categoryId)) {
          newCategoryFeatures.push({
            globalFeatureId: featureId,
            categoryId: categoryId
          });
        }
      } else {
        const index = newCategoryFeatures.findIndex(f => 
          f.globalFeatureId === featureId && f.categoryId === categoryId
        );
        if (index !== -1) {
          newCategoryFeatures.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [tierType]: {
          ...currentTier,
          categoryFeatures: newCategoryFeatures
        }
      };
    });
  };

  const handleAdditionalFeatureToggle = (tierType, categoryId, featureId, isChecked) => {    
    setTierData(prev => {
      const currentTier = prev[tierType];
      const newAdditionalFeatures = [...(currentTier.additionalFeatures || [])];
      
      if (isChecked) {
        if (!newAdditionalFeatures.some(f => f.additionalFeatureId === featureId && f.categoryId === categoryId)) {
          newAdditionalFeatures.push({
            additionalFeatureId: featureId,
            categoryId: categoryId
          });
        }
      } else {
        const index = newAdditionalFeatures.findIndex(f => 
          f.additionalFeatureId === featureId && f.categoryId === categoryId
        );
        if (index !== -1) {
          newAdditionalFeatures.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        [tierType]: {
          ...currentTier,
          additionalFeatures: newAdditionalFeatures
        }
      };
    });
  };

  const handleDurationChange = (durationId) => {
    if (isEditMode) return;
    
    setSelectedDuration(durationId);
    setTierData({
      Low: createEmptyTier('Low'),
      Mid: createEmptyTier('Mid'),
      High: createEmptyTier('High')
    });
  };

  const handleCountryChange = (countryId) => {
    if (isEditMode) return;

    setSelectedCountry(countryId);
    setTierData({
      Low: createEmptyTier('Low'),
      Mid: createEmptyTier('Mid'),
      High: createEmptyTier('High')
    });
  };

  const handleTierFieldChange = (tierType, field, value) => {    
    setTierData(prev => {
      const updatedTier = {
        ...prev[tierType],
        [field]: value
      };

      if (field === 'originalPrice' || field === 'flatDiscount' || field === 'percentageDiscount' || field === 'discountType') {
        const originalPrice = field === 'originalPrice' ? value : updatedTier.originalPrice;
        const discountType = field === 'discountType' ? value : updatedTier.discountType;
        const flatDiscount = field === 'flatDiscount' ? value : updatedTier.flatDiscount;
        const percentageDiscount = field === 'percentageDiscount' ? value : updatedTier.percentageDiscount;

        updatedTier.currentPrice = calculateCurrentPrice(
          originalPrice,
          discountType,
          discountType === 'flat' ? flatDiscount : percentageDiscount
        );
      }

      return {
        ...prev,
        [tierType]: updatedTier
      };
    });
  };

  const handleSave = async () => {    
    if (!validateForm()) return;

    try {
      const tiersToSave = [];
      Object.entries(tierData).forEach(([tierType, tierInfo]) => {
        if (tierInfo.originalPrice > 0 || tierInfo.categoryFeatures.length > 0 || tierInfo.additionalFeatures.length > 0) {
          let discountType = tierInfo.discountType;
          let discountValue = 0;
          
          if (tierInfo.discountType === DiscountTypes.FLAT) {
            discountValue = tierInfo.flatDiscount;
          } else if (tierInfo.discountType === DiscountTypes.PERCENTAGE) {
            discountValue = tierInfo.percentageDiscount;
          }
          
          const calculatedCurrentPrice = calculateCurrentPrice(
            tierInfo.originalPrice,
            discountType,
            discountValue
          );

          const newTier = {
            ...(isEditMode && tierInfo.id && { id: tierInfo.id }),
            
            tierType: tierType,
            name: tierInfo.name || `${tierType} Tier`,
            description: tierInfo.description,
            isPopular: tierInfo.isPopular,
            durationId: selectedDuration,
            countryId: selectedCountry,
            
            originalPrice: Number(tierInfo.originalPrice.toFixed(2)),
            currentPrice: Number(calculatedCurrentPrice.toFixed(2)),
            discountType: discountType,
            discountValue: discountValue,
            
            categoryFeatures: tierInfo.categoryFeatures,
            additionalFeatures: tierInfo.additionalFeatures,
            featuresConfiguration: [
              ...tierInfo.categoryFeatures.map(f => ({
                categoryId: f.categoryId,
                featureType: 'global',
                featureId: f.globalFeatureId,
                customPrice: 0
              })),
              ...tierInfo.additionalFeatures.map(f => ({
                categoryId: f.categoryId,
                featureType: 'additional',
                featureId: f.additionalFeatureId,
                customPrice: 0
              }))
            ]
          };
          
          tiersToSave.push(newTier);
        }
      });

      if (tiersToSave.length === 0) {
        Notify.failure('Please configure at least one tier before saving');
        return;
      }

      if (isEditMode) {
        await dispatch(updateValuePackTiers({
          id: valuePackId,
          tiers: tiersToSave
        })).unwrap();
      } else {
        await dispatch(saveValuePackTiers({
          id: valuePackId,
          tiersData: tiersToSave
        })).unwrap();
      }

      Notify.success(isEditMode ? 'Tiers updated successfully' : 'Tiers created successfully');
      navigate(`/value-pack/${valuePackId}/tiers`);
    } catch (error) {
      console.error('Save tiers error:', error);
      Notify.failure('Failed to save tiers: ' + (error.message || 'Unknown error'));
    }
  };

  const validateForm = () => {
    if (!selectedDuration || !selectedCountry) {
      Notify.failure('Please select duration and country');
      return false;
    }

    const hasValidTier = Object.values(tierData).some(tier => 
      tier.originalPrice > 0 || tier.categoryFeatures.length > 0 || tier.additionalFeatures.length > 0
    );

    if (!hasValidTier) {
      Notify.failure('Please configure at least one tier with pricing or features');
      return false;
    }

    for (const tierType in tierData) {
      const tier = tierData[tierType];
      
      if (tier.originalPrice <= 0) continue;

      if (tier.discountType === DiscountTypes.PERCENTAGE) {
        const discountValue = tier.percentageDiscount;
        if (discountValue < 0 || discountValue > 100) {
          Notify.failure(`Percentage discount for ${tierType} tier must be between 0 and 100`);
          return false;
        }
      }
      
      if (tier.discountType === DiscountTypes.FLAT) {
        const discountValue = tier.flatDiscount;
        if (discountValue < 0) {
          Notify.failure(`Flat discount for ${tierType} tier cannot be negative`);
          return false;
        }
        if (discountValue > tier.originalPrice) {
          Notify.failure(`Flat discount for ${tierType} tier cannot exceed original price`);
          return false;
        }
      }

      if (tier.currentPrice > tier.originalPrice) {
        Notify.failure(`Current price for ${tierType} tier cannot be greater than original price`);
        return false;
      }
    }

    return true;
  };

  const calculateCurrentPrice = (originalPrice, discountType, discountValue) => {
    if (!discountType || !discountValue || discountValue <= 0 || !originalPrice) {
      return originalPrice || 0;
    }
    
    if (discountType === DiscountTypes.FLAT) {
      return Math.max(0, originalPrice - discountValue);
    }
    
    if (discountType === DiscountTypes.PERCENTAGE) {
      const discountAmount = (originalPrice * discountValue) / 100;
      return Math.max(0, originalPrice - discountAmount);
    }
    
    return originalPrice;
  };

  return {
    loading,
    isEditMode,
    selectedDuration,
    selectedCountry,
    tierData,
    setTierData,
    handleDurationChange,
    handleCountryChange,
    handleTierFieldChange,
    handleFeatureToggle,
    handleSave,
    calculateCurrentPrice
  };
};
